import { useState, useRef, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import toast from "react-hot-toast";
import clsx from "clsx";

/* ── Constants ──────────────────────────────────────────────────────────── */
const GB = 1024 ** 3;
const BACKEND_URL = (import.meta.env.VITE_API_URL || "").replace("/api", "");

const CATEGORIES = [
  { key: "all",      label: "Alle",       icon: "📂", color: "#8b5cf6" },
  { key: "image",    label: "Bilder",     icon: "🖼️", color: "#22c55e" },
  { key: "document", label: "Dokumente",  icon: "📄", color: "#3b82f6" },
  { key: "video",    label: "Videos",     icon: "🎬", color: "#ec4899" },
  { key: "audio",    label: "Audio",      icon: "🎵", color: "#f59e0b" },
  { key: "archive",  label: "Archive",    icon: "📦", color: "#f97316" },
  { key: "other",    label: "Sonstiges",  icon: "📁", color: "#6b7280" },
];

const SORTS = [
  { key: "date_desc", label: "Neueste zuerst" },
  { key: "date_asc",  label: "Älteste zuerst" },
  { key: "name_asc",  label: "Name A–Z" },
  { key: "size_desc", label: "Größte zuerst" },
];

function getMimeCategory(mime = "") {
  if (mime.startsWith("image/"))  return "image";
  if (mime.startsWith("video/"))  return "video";
  if (mime.startsWith("audio/"))  return "audio";
  if (mime.startsWith("text/") || mime === "application/pdf" ||
      mime.includes("word") || mime.includes("spreadsheet") ||
      mime.includes("presentation") || mime.includes("document"))
    return "document";
  if (mime.includes("zip") || mime.includes("tar") || mime.includes("rar") ||
      mime.includes("7z") || mime.includes("gzip"))
    return "archive";
  return "other";
}
const catMeta = (mime) => CATEGORIES.find((c) => c.key === getMimeCategory(mime)) || CATEGORIES.at(-1);

const fmtBytes = (b) => {
  if (!b) return "0 B";
  if (b < 1024)       return `${b} B`;
  if (b < 1024 ** 2)  return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3)  return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
};
const uid = () => Math.random().toString(36).slice(2, 10);

/* ── Skeleton ────────────────────────────────────────────────────────────── */
function Skeleton({ className }) {
  return <div className={clsx("animate-pulse rounded-xl bg-white/5", className)} />;
}
function VaultSkeleton() {
  return (
    <div className="space-y-4 animate-fade-in">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-28 w-full" />
      <div className="flex gap-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-20 rounded-full" />)}</div>
      <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
    </div>
  );
}

/* ── Storage bar — segmented by category, richer breakdown ─────────────────── */
function StorageBar({ usedBytes, maxBytes, byCategory, onRequest }) {
  const pct = Math.min((usedBytes / maxBytes) * 100, 100);
  const freeBytes = Math.max(0, maxBytes - usedBytes);
  const warnColor = pct > 90 ? "#ef4444" : pct > 70 ? "#f59e0b" : null;

  return (
    <div className="relative overflow-hidden bg-white/5 border border-white/10 rounded-2xl p-5">
      <div className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full bg-violet-500/10 blur-3xl" />
      <div className="relative flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center text-lg">🔐</div>
          <div>
            <p className="text-sm font-black text-white">Speicher</p>
            <p className="text-[11px] text-gray-400">{fmtBytes(usedBytes)} von {fmtBytes(maxBytes)} belegt</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-black" style={{ color: warnColor || "#a78bfa" }}>{pct.toFixed(1)}%</p>
          <p className="text-[10px] text-gray-500">{fmtBytes(freeBytes)} frei</p>
        </div>
      </div>

      {/* segmented bar: one colored slice per category, proportional to bytes used */}
      <div className="relative h-3 w-full rounded-full overflow-hidden bg-white/10 flex">
        {CATEGORIES.filter((c) => c.key !== "all" && byCategory[c.key]?.bytes).map((c) => (
          <div key={c.key} title={`${c.label}: ${fmtBytes(byCategory[c.key].bytes)}`}
            className="h-full transition-all duration-700"
            style={{ width: `${(byCategory[c.key].bytes / maxBytes) * 100}%`, background: c.color }} />
        ))}
      </div>

      {/* legend */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {CATEGORIES.filter((c) => c.key !== "all" && byCategory[c.key]?.bytes).map((c) => (
          <span key={c.key} className="flex items-center gap-1.5 text-[10px] text-gray-400">
            <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
            {c.label} · {fmtBytes(byCategory[c.key].bytes)}
          </span>
        ))}
      </div>

      {pct > 80 && (
        <div className="relative mt-3 flex items-center justify-between text-[11px]">
          <span className="text-yellow-400 font-bold">⚠️ Nur noch {fmtBytes(freeBytes)} frei</span>
          <button onClick={onRequest} className="text-violet-400 hover:text-violet-300 font-bold underline">Mehr anfordern →</button>
        </div>
      )}
    </div>
  );
}

/* ── Category tabs ────────────────────────────────────────────────────────── */
function CategoryTabs({ active, counts, onChange }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
      {CATEGORIES.map((c) => {
        const count = c.key === "all" ? counts.total : (counts[c.key] || 0);
        const isActive = active === c.key;
        return (
          <button key={c.key} onClick={() => onChange(c.key)}
            className={clsx("flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-200",
              isActive ? "text-white shadow-lg" : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-200")}
            style={isActive ? { background: c.color, boxShadow: `0 4px 16px ${c.color}55` } : undefined}>
            <span>{c.icon}</span><span>{c.label}</span>
            {count > 0 && <span className={clsx("text-[10px] px-1.5 py-0.5 rounded-full", isActive ? "bg-black/20" : "bg-white/10")}>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ── File card (grid) ────────────────────────────────────────────────────── */
function FileCardGrid({ file, selectMode, selected, onToggleSelect, onDelete, onPreview, onRename }) {
  const meta = catMeta(file.mimeType);
  const isImage = file.mimeType?.startsWith("image/");
  const isVideo = file.mimeType?.startsWith("video/");
  const downloadUrl = `${BACKEND_URL}/uploads/${file.storedName}`;

  return (
    <div className={clsx("group relative bg-white/5 border rounded-2xl p-4 transition-all duration-200 flex flex-col gap-3",
      selected ? "border-violet-500 bg-violet-500/10" : "border-white/10 hover:border-violet-500/40 hover:bg-white/8 hover:-translate-y-0.5")}>
      {selectMode && (
        <button onClick={() => onToggleSelect(file.id)}
          className={clsx("absolute top-2.5 left-2.5 z-10 w-5 h-5 rounded-md border-2 flex items-center justify-center text-[10px] font-black transition-all",
            selected ? "bg-violet-600 border-violet-600 text-white" : "border-white/30 bg-black/40")}>
          {selected ? "✓" : ""}
        </button>
      )}
      <div className="w-full aspect-square rounded-xl bg-white/5 flex items-center justify-center overflow-hidden cursor-pointer relative"
        onClick={() => (selectMode ? onToggleSelect(file.id) : onPreview(file))}>
        {isImage ? (
          <img src={downloadUrl} alt={file.originalName} className="w-full h-full object-cover rounded-xl" loading="lazy"
            onError={(e) => { e.target.style.display = "none"; e.target.parentNode.innerHTML = "<span style='font-size:2.5rem'>🖼️</span>"; }} />
        ) : (
          <span className="text-4xl" style={{ filter: `drop-shadow(0 0 12px ${meta.color}55)` }}>{meta.icon}</span>
        )}
        {isVideo && <span className="absolute bottom-1.5 right-1.5 text-[10px] bg-black/60 rounded px-1.5 py-0.5">▶</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-white truncate" title={file.originalName}>{file.originalName}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">{fmtBytes(file.size)} · {new Date(file.createdAt).toLocaleDateString("de-DE")}</p>
      </div>
      {!selectMode && (
        <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <a href={downloadUrl} download={file.originalName} className="flex-1 py-1.5 text-center text-[11px] font-bold bg-violet-600/20 hover:bg-violet-600/40 text-violet-300 rounded-lg transition-colors">⬇️</a>
          <button onClick={() => onRename(file)} className="px-2.5 py-1.5 text-[11px] font-bold bg-cyan-500/10 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition-colors">✏️</button>
          <button onClick={() => onDelete(file)} className="px-2.5 py-1.5 text-[11px] font-bold bg-red-500/10 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors">🗑️</button>
        </div>
      )}
    </div>
  );
}

/* ── File row (list) ─────────────────────────────────────────────────────── */
function FileCardList({ file, selectMode, selected, onToggleSelect, onDelete, onPreview, onRename }) {
  const meta = catMeta(file.mimeType);
  const downloadUrl = `${BACKEND_URL}/uploads/${file.storedName}`;

  return (
    <div className={clsx("group flex items-center gap-3 border rounded-xl px-4 py-3 transition-all duration-200",
      selected ? "border-violet-500 bg-violet-500/10" : "bg-white/5 border-white/10 hover:border-violet-500/40 hover:bg-white/8")}>
      {selectMode && (
        <button onClick={() => onToggleSelect(file.id)}
          className={clsx("flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center text-[10px] font-black transition-all",
            selected ? "bg-violet-600 border-violet-600 text-white" : "border-white/25")}>
          {selected ? "✓" : ""}
        </button>
      )}
      <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0 cursor-pointer text-xl"
        style={{ boxShadow: `inset 0 0 0 1px ${meta.color}33` }}
        onClick={() => (selectMode ? onToggleSelect(file.id) : onPreview(file))}>
        {meta.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate">{file.originalName}</p>
        <p className="text-[11px] text-gray-500">{fmtBytes(file.size)} · {new Date(file.createdAt).toLocaleDateString("de-DE")}</p>
      </div>
      {!selectMode && (
        <div className="flex items-center gap-1.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
          <a href={downloadUrl} download={file.originalName} className="px-3 py-1.5 text-[11px] font-bold bg-violet-600/20 hover:bg-violet-600/40 text-violet-300 rounded-lg transition-colors">⬇️</a>
          <button onClick={() => onRename(file)} className="px-3 py-1.5 text-[11px] font-bold bg-cyan-500/10 hover:bg-cyan-500/30 text-cyan-300 rounded-lg transition-colors">✏️</button>
          <button onClick={() => onDelete(file)} className="px-3 py-1.5 text-[11px] font-bold bg-red-500/10 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors">🗑️</button>
        </div>
      )}
    </div>
  );
}

/* ── Drop zone (compact, always visible) ─────────────────────────────────── */
function DropZone({ onFiles }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  return (
    <div
      onDrop={(e) => { e.preventDefault(); setDragging(false); const f = Array.from(e.dataTransfer.files); if (f.length) onFiles(f); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onClick={() => inputRef.current?.click()}
      className={clsx("relative border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all duration-200",
        dragging ? "border-violet-500 bg-violet-500/10 scale-[1.01]" : "border-white/20 hover:border-violet-500/60 hover:bg-white/5")}>
      <input ref={inputRef} type="file" multiple className="hidden"
        onChange={(e) => { const f = Array.from(e.target.files || []); if (f.length) onFiles(f); e.target.value = ""; }} />
      <div className="flex flex-col items-center gap-2">
        <div className={clsx("w-12 h-12 rounded-2xl flex items-center justify-center text-2xl transition-all duration-200", dragging ? "bg-violet-500/30 scale-110" : "bg-white/5")}>
          {dragging ? "📂" : "⬆️"}
        </div>
        <div>
          <p className="text-sm font-bold text-white">{dragging ? "Hier loslassen!" : "Dateien hierher ziehen"}</p>
          <p className="text-[11px] text-gray-500 mt-0.5">oder klicken zum Auswählen · überall auf der Seite droppen geht auch</p>
        </div>
      </div>
    </div>
  );
}

/* ── Upload queue — real per-file progress ───────────────────────────────── */
function UploadQueue({ uploads, onDismiss }) {
  if (uploads.length === 0) return null;
  return (
    <div className="space-y-2">
      {uploads.map((u) => (
        <div key={u.id} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5">
          <span className="text-lg flex-shrink-0">{u.status === "done" ? "✅" : u.status === "error" ? "⚠️" : "⬆️"}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-white truncate">{u.file.name}</p>
            <div className="mt-1 h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-200"
                style={{ width: `${u.progress}%`, background: u.status === "error" ? "#ef4444" : u.status === "done" ? "#22c55e" : "linear-gradient(90deg,#8b5cf6,#06b6d4)" }} />
            </div>
            {u.status === "error" && <p className="text-[10px] text-red-400 mt-0.5">{u.error}</p>}
          </div>
          <span className="text-[10px] text-gray-500 flex-shrink-0 w-9 text-right">{u.status === "done" ? "100%" : u.status === "error" ? "—" : `${u.progress}%`}</span>
          {(u.status === "done" || u.status === "error") && (
            <button onClick={() => onDismiss(u.id)} className="text-gray-600 hover:text-gray-300 flex-shrink-0">✕</button>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Preview modal ───────────────────────────────────────────────────────── */
function PreviewModal({ file, onClose }) {
  if (!file) return null;
  const downloadUrl = `${BACKEND_URL}/uploads/${file.storedName}`;
  const isImage = file.mimeType?.startsWith("image/");
  const isVideo = file.mimeType?.startsWith("video/");
  const isAudio = file.mimeType?.startsWith("audio/");
  const isPDF   = file.mimeType === "application/pdf";

  return (
    <Modal open={!!file} onClose={onClose} title={file.originalName}>
      <div className="space-y-4">
        <div className="bg-white/5 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px] max-h-[400px]">
          {isImage && <img src={downloadUrl} alt={file.originalName} className="max-w-full max-h-[380px] object-contain rounded-xl" />}
          {isVideo && <video src={downloadUrl} controls className="max-w-full max-h-[380px] rounded-xl" />}
          {isAudio && <div className="p-8 flex flex-col items-center gap-4"><span className="text-6xl">🎵</span><audio src={downloadUrl} controls className="w-full" /></div>}
          {isPDF && <iframe src={downloadUrl} className="w-full h-[380px] rounded-xl" title={file.originalName} />}
          {!isImage && !isVideo && !isAudio && !isPDF && (
            <div className="p-8 text-center"><div className="text-6xl mb-3">{catMeta(file.mimeType).icon}</div><p className="text-sm text-gray-400">Vorschau nicht verfügbar</p></div>
          )}
        </div>
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{fmtBytes(file.size)}</span>
          <span>{new Date(file.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}</span>
        </div>
        <a href={downloadUrl} download={file.originalName} className="flex items-center justify-center gap-2 w-full py-2.5 bg-violet-600 hover:bg-violet-500 text-white font-bold text-sm rounded-xl transition-colors">⬇️ Herunterladen</a>
      </div>
    </Modal>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function Vault() {
  const qc = useQueryClient();
  const [category, setCategory]     = useState("all");
  const [search, setSearch]         = useState("");
  const [sort, setSort]             = useState("date_desc");
  const [view, setView]             = useState("list");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewFile, setPreviewFile]   = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue]   = useState("");
  const [reqModal, setReqModal]     = useState(false);
  const [reqNote, setReqNote]       = useState("");
  const [uploads, setUploads]       = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected]     = useState(new Set());
  const [pageDrag, setPageDrag]     = useState(false);
  const dragCounter = useRef(0);

  const { data, isLoading } = useQuery({
    queryKey: ["vault"],
    queryFn: () => api.get("/vault").then((r) => r.data),
    staleTime: 30_000,
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/vault/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vault"] }); },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }) => api.patch(`/vault/${id}/rename`, { name }),
    onSuccess: () => {
      toast.success("Umbenannt ✏️");
      qc.invalidateQueries({ queryKey: ["vault"] });
      setRenameTarget(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || "Umbenennen fehlgeschlagen"),
  });

  const requestStorage = useMutation({
    mutationFn: () => api.post("/vault/request-storage", { note: reqNote }),
    onSuccess: () => { toast.success("Anfrage gesendet! 📬"); setReqModal(false); setReqNote(""); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  /* real per-file upload with progress, uploaded concurrently */
  const handleFiles = useCallback((files) => {
    const entries = files.map((file) => ({ id: uid(), file, progress: 0, status: "uploading", error: null }));
    setUploads((u) => [...entries, ...u]);

    entries.forEach(async (entry) => {
      const fd = new FormData();
      fd.append("file", entry.file);
      try {
        await api.post("/vault/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            const pct = evt.total ? Math.round((evt.loaded * 100) / evt.total) : 0;
            setUploads((u) => u.map((x) => (x.id === entry.id ? { ...x, progress: pct } : x)));
          },
        });
        setUploads((u) => u.map((x) => (x.id === entry.id ? { ...x, progress: 100, status: "done" } : x)));
        qc.invalidateQueries({ queryKey: ["vault"] });
        setTimeout(() => setUploads((u) => u.filter((x) => x.id !== entry.id)), 2500);
      } catch (err) {
        setUploads((u) => u.map((x) => (x.id === entry.id ? { ...x, status: "error", error: err.response?.data?.error || "Fehlgeschlagen" } : x)));
      }
    });
  }, [qc]);

  // full-page drag & drop overlay
  useEffect(() => {
    const onEnter = (e) => { e.preventDefault(); dragCounter.current++; if (e.dataTransfer?.types?.includes("Files")) setPageDrag(true); };
    const onLeave = (e) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current <= 0) { dragCounter.current = 0; setPageDrag(false); } };
    const onOver  = (e) => e.preventDefault();
    const onDrop  = (e) => {
      e.preventDefault(); dragCounter.current = 0; setPageDrag(false);
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length) handleFiles(files);
    };
    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [handleFiles]);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const bulkDelete = async () => {
    const ids = [...selected];
    await Promise.all(ids.map((id) => api.delete(`/vault/${id}`).catch(() => null)));
    toast.success(`${ids.length} Datei${ids.length > 1 ? "en" : ""} gelöscht`);
    qc.invalidateQueries({ queryKey: ["vault"] });
    exitSelectMode();
  };
  const bulkDownload = () => {
    const files = allFiles.filter((f) => selected.has(f.id));
    files.forEach((f, i) => setTimeout(() => {
      const a = document.createElement("a");
      a.href = `${BACKEND_URL}/uploads/${f.storedName}`; a.download = f.originalName; a.click();
    }, i * 350)); // stagger so the browser doesn't block multiple simultaneous downloads
  };

  if (isLoading) return <VaultSkeleton />;

  const allFiles    = data?.files || [];
  const usedBytes   = data?.usedBytes || 0;
  const maxBytes    = (data?.maxGB || 10) * GB;

  const counts = { total: allFiles.length };
  const byCategory = {};
  allFiles.forEach((f) => {
    const cat = getMimeCategory(f.mimeType);
    counts[cat] = (counts[cat] || 0) + 1;
    byCategory[cat] = byCategory[cat] || { bytes: 0 };
    byCategory[cat].bytes += f.size;
  });

  let filtered = allFiles.filter((f) => {
    if (category !== "all" && getMimeCategory(f.mimeType) !== category) return false;
    if (search && !f.originalName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  filtered = [...filtered].sort((a, b) => {
    if (sort === "date_desc") return new Date(b.createdAt) - new Date(a.createdAt);
    if (sort === "date_asc")  return new Date(a.createdAt) - new Date(b.createdAt);
    if (sort === "name_asc")  return a.originalName.localeCompare(b.originalName);
    if (sort === "size_desc") return b.size - a.size;
    return 0;
  });

  return (
    <div className="relative space-y-5 animate-fade-in max-w-3xl mx-auto pb-24">
      {/* full-page drop overlay */}
      {pageDrag && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center backdrop-blur-sm" style={{ background: "rgba(6,6,26,0.85)" }}>
          <div className="rounded-3xl border-2 border-dashed border-violet-500 bg-violet-500/10 px-16 py-14 text-center">
            <div className="text-6xl mb-3">📂</div>
            <p className="text-xl font-black text-white">Überall auf der Seite loslassen</p>
            <p className="text-sm text-violet-300 mt-1">Deine Dateien landen sicher im Tresor</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-black flex items-center gap-2"><span className="text-2xl">🔐</span><span>Flow Tresor</span></h1>
          <p className="text-xs text-gray-400 mt-0.5">Deine private, sichere Dateiablage</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={clsx("px-3 py-1.5 rounded-xl text-xs font-bold transition-all", selectMode ? "bg-violet-600 text-white" : "bg-white/5 text-gray-400 hover:text-gray-200")}>
            {selectMode ? "✕ Fertig" : "☑️ Auswählen"}
          </button>
          <div className="flex gap-1 bg-white/5 rounded-xl p-1">
            <button onClick={() => setView("list")} className={clsx("px-3 py-1.5 rounded-lg text-xs font-bold transition-all", view === "list" ? "bg-violet-600 text-white" : "text-gray-400 hover:text-gray-200")}>☰</button>
            <button onClick={() => setView("grid")} className={clsx("px-3 py-1.5 rounded-lg text-xs font-bold transition-all", view === "grid" ? "bg-violet-600 text-white" : "text-gray-400 hover:text-gray-200")}>⊞</button>
          </div>
        </div>
      </div>

      <StorageBar usedBytes={usedBytes} maxBytes={maxBytes} byCategory={byCategory} onRequest={() => setReqModal(true)} />

      <DropZone onFiles={handleFiles} />
      <UploadQueue uploads={uploads} onDismiss={(id) => setUploads((u) => u.filter((x) => x.id !== id))} />

      <CategoryTabs active={category} counts={counts} onChange={setCategory} />

      <div className="flex gap-2">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">🔍</span>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Datei suchen…"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors" />
          {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">✕</button>}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-gray-300 focus:outline-none focus:border-violet-500 cursor-pointer">
          {SORTS.map((s) => <option key={s.key} value={s.key} className="bg-gray-900">{s.label}</option>)}
        </select>
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-500">{filtered.length} {filtered.length === 1 ? "Datei" : "Dateien"} · {fmtBytes(filtered.reduce((s, f) => s + f.size, 0))}</p>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center text-4xl">{search ? "🔍" : CATEGORIES.find((c) => c.key === category)?.icon || "📂"}</div>
          <div className="text-center">
            <p className="font-bold text-gray-300">{search ? "Keine Treffer" : "Noch leer"}</p>
            <p className="text-xs text-gray-500 mt-1">{search ? `Keine Dateien für "${search}" gefunden` : "Ziehe Dateien hierher oder irgendwo auf die Seite"}</p>
          </div>
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {filtered.map((f) => (
            <FileCardGrid key={f.id} file={f} selectMode={selectMode} selected={selected.has(f.id)} onToggleSelect={toggleSelect}
              onDelete={setDeleteTarget} onPreview={setPreviewFile} onRename={(file) => { setRenameTarget(file); setRenameValue(file.originalName); }} />
          ))}
        </div>
      ) : (
        <div className="space-y-1.5">
          {filtered.map((f) => (
            <FileCardList key={f.id} file={f} selectMode={selectMode} selected={selected.has(f.id)} onToggleSelect={toggleSelect}
              onDelete={setDeleteTarget} onPreview={setPreviewFile} onRename={(file) => { setRenameTarget(file); setRenameValue(file.originalName); }} />
          ))}
        </div>
      )}

      <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-2xl p-4">
        <p className="text-xs text-cyan-300 font-bold mb-2">ℹ️ Tresor Info</p>
        <ul className="text-[11px] text-gray-400 space-y-1">
          <li>• Dateien sind nur für dich sichtbar — sicher verschlüsselt</li>
          <li>• {data?.maxGB || 10} GB kostenlos — erweiterbar auf Anfrage</li>
          <li>• Kein .exe, .bat, .sh oder andere ausführbare Dateien</li>
          <li>• Dateien bleiben dauerhaft erhalten</li>
        </ul>
      </div>

      {/* floating bulk-action bar */}
      {selectMode && selected.size > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-2xl border border-violet-500/40 bg-[#13133a] px-4 py-3 shadow-2xl shadow-violet-900/40">
          <span className="text-xs font-bold text-white mr-1">{selected.size} ausgewählt</span>
          <button onClick={bulkDownload} className="px-3 py-1.5 rounded-xl text-xs font-bold bg-violet-600/30 text-violet-200 hover:bg-violet-600/50 transition-colors">⬇️ Download</button>
          <button onClick={bulkDelete} className="px-3 py-1.5 rounded-xl text-xs font-bold bg-red-500/20 text-red-300 hover:bg-red-500/40 transition-colors">🗑️ Löschen</button>
        </div>
      )}

      <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Datei löschen?">
        {deleteTarget && (
          <div className="space-y-4">
            <div className="bg-white/5 rounded-2xl p-5 text-center">
              <div className="text-5xl mb-3">{catMeta(deleteTarget.mimeType).icon}</div>
              <p className="font-bold text-sm text-white">{deleteTarget.originalName}</p>
              <p className="text-xs text-gray-400 mt-1">{fmtBytes(deleteTarget.size)}</p>
            </div>
            <p className="text-sm text-red-400 text-center font-bold">⚠️ Diese Aktion kann nicht rückgängig gemacht werden!</p>
            <div className="flex gap-2">
              <Button full variant="danger" loading={del.isPending} onClick={() => del.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) })}>🗑️ Endgültig löschen</Button>
              <Button full variant="secondary" onClick={() => setDeleteTarget(null)}>Abbrechen</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!renameTarget} onClose={() => setRenameTarget(null)} title="✏️ Datei umbenennen">
        {renameTarget && (
          <div className="space-y-4">
            <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && renameValue.trim()) rename.mutate({ id: renameTarget.id, name: renameValue.trim() }); }}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500" />
            <Button full loading={rename.isPending} disabled={!renameValue.trim()} onClick={() => rename.mutate({ id: renameTarget.id, name: renameValue.trim() })}>Speichern</Button>
          </div>
        )}
      </Modal>

      <Modal open={reqModal} onClose={() => setReqModal(false)} title="📬 Mehr Speicher anfordern">
        <div className="space-y-4">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <div className="text-3xl mb-2">💾</div>
            <p className="text-sm text-gray-300">Aktuell: <span className="text-white font-bold">{fmtBytes(usedBytes)}</span> von <span className="text-violet-400 font-bold">{data?.maxGB || 10} GB</span></p>
          </div>
          <p className="text-sm text-gray-400">Dein Lehrer wird benachrichtigt und kann dir zusätzliche 5 GB freischalten.</p>
          <textarea value={reqNote} onChange={(e) => setReqNote(e.target.value)} placeholder="Warum brauchst du mehr Speicher? (optional)"
            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none h-24 focus:outline-none focus:border-violet-500" />
          <Button full loading={requestStorage.isPending} onClick={() => requestStorage.mutate()}>📬 Anfrage senden</Button>
        </div>
      </Modal>
    </div>
  );
}
